import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MatMapDialog } from "./MatMapDialog";
import { motion } from "framer-motion";
import { api, API } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const Initials = ({ name }) => (
  <div className="w-full h-full flex items-center justify-center bg-[#111] text-neon font-display text-6xl uppercase">
    {(name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
  </div>
);

export const Instructors = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [coach, setCoach] = useState(null);
  const [coachClasses, setCoachClasses] = useState(null);
  const [booking, setBooking] = useState(null);
  const classesRef = useRef(null);

  const loadClasses = useCallback(async () => {
    const { data } = await api.get("/classes");
    classesRef.current = data;
    return data;
  }, []);

  // prefetch instructors + weekly classes together so a coach tap is instant
  useEffect(() => {
    api.get("/instructors").then((r) => setList(r.data)).catch(() => {});
    loadClasses().catch(() => {});
  }, [loadClasses]);

  const forCoach = (all, i) => {
    const mine = all.filter((c) => (c.instructor || "").toLowerCase() === i.name.toLowerCase());
    mine.sort((a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day) || (a.time_sort || 0) - (b.time_sort || 0));
    return mine;
  };

  const openCoach = (i) => {
    setCoach(i);
    setCoachClasses(classesRef.current ? forCoach(classesRef.current, i) : null);
    // refresh spot counts in the background
    loadClasses().then((all) => setCoachClasses(forCoach(all, i))).catch(() => { if (!classesRef.current) setCoachClasses([]); });
  };

  const bookClass = (c) => {
    if (!user) { navigate("/login"); return; }
    setCoach(null);
    setBooking(c);
  };

  if (!list.length) return null;

  return (
    <section id="instructors" data-testid="instructors-section" className="relative bg-black py-24 sm:py-32 border-t border-neutral-900">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-14">
          <p className="text-xs uppercase tracking-[0.35em] text-neon mb-5">The Coaches</p>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
            Meet your <span className="text-stroke-neon">instructors</span>
          </h2>
          <p className="text-sm text-neutral-500 mt-4">Tap a coach to see their weekly classes and book a mat.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1">
          {list.map((i, idx) => (
            <motion.button
              type="button"
              key={i.instructor_id}
              onClick={() => openCoach(i)}
              data-testid={`instructor-card-${i.instructor_id}`}
              aria-label={`View ${i.name}'s class schedule`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
              className="group text-left border border-neutral-900 hover:border-neon transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-neon"
            >
              <div className="aspect-[4/5] overflow-hidden">
                {i.has_photo ? (
                  <img
                    src={`${API}/instructors/${i.instructor_id}/photo?v=${encodeURIComponent(i.photo_v)}`}
                    alt={i.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                ) : (
                  <Initials name={i.name} />
                )}
              </div>
              <div className="p-5">
                <h3 className="font-display uppercase text-2xl text-white">{i.name}</h3>
                <p className="text-sm text-neutral-400 font-light mt-1 leading-relaxed">
                  {i.bio || "Mat Pilates coach at Sculpt Fusion."}
                </p>
                <span className="inline-block mt-3 text-[10px] uppercase tracking-[0.2em] text-neon opacity-0 group-hover:opacity-100 transition-opacity">
                  View classes →
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!coach} onOpenChange={(o) => !o && setCoach(null)}>
        <DialogContent data-testid="coach-schedule-dialog" className="bg-[#0A0A0A] border-neutral-800 text-white max-w-lg flex flex-col max-h-[85vh]">
          {coach && (
            <>
              <DialogHeader className="shrink-0">
                <DialogTitle className="font-display uppercase text-3xl leading-none">{coach.name}'s classes</DialogTitle>
                <DialogDescription className="text-neutral-400 line-clamp-3">
                  {coach.bio || "Weekly classes coached by " + coach.name + "."}
                </DialogDescription>
              </DialogHeader>
              {coachClasses === null ? (
                <p className="text-neutral-500 py-6 text-sm">Loading schedule…</p>
              ) : coachClasses.length === 0 ? (
                <p className="text-neutral-400 py-6 text-sm" data-testid="coach-no-classes">
                  No classes on the weekly schedule for {coach.name} right now.
                </p>
              ) : (
                <div className="flex flex-col gap-1 overflow-y-auto min-h-0 pr-1" data-testid="coach-class-list"
                  style={{ WebkitOverflowScrolling: "touch" }}>
                  {coachClasses.map((c) => (
                    <div key={c.class_id} data-testid={`coach-class-${c.class_id}`}
                      className="flex items-center justify-between gap-3 border border-neutral-900 px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm text-white">{c.class_name}</p>
                        <p className="text-xs text-neutral-400">
                          {c.day} · {c.time}{typeof c.spots_left === "number" ? ` · ${c.spots_left}/${c.spots || 16} open` : ""}
                        </p>
                      </div>
                      {c.cancelled ? (
                        <span className="shrink-0 text-[10px] uppercase tracking-wider text-neutral-500 border border-neutral-800 px-3 py-2">Cancelled</span>
                      ) : c.spots_left === 0 ? (
                        <span className="shrink-0 text-[10px] uppercase tracking-wider text-neutral-400 border border-neutral-800 px-3 py-2">Full</span>
                      ) : (
                        <button onClick={() => bookClass(c)} data-testid={`coach-book-${c.class_id}`}
                          className="shrink-0 bg-neon text-black font-semibold text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 hover:bg-white transition-colors">
                          Book
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <MatMapDialog cls={booking} date={booking?.date || null} onClose={() => setBooking(null)} onChanged={() => loadClasses().catch(() => {})} />
    </section>
  );
};
